import { Link } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { clsBouton, Fil, Entete } from './partages';
import { BOUTIQUES, REGLAGES } from '../data/mock-data';

/** NOS BOUTIQUES — l'adresse, l'horaire du jour, le téléphone. Rien d'autre ne compte. */

/** « Ouvert · ferme à 19h30 » se calcule sur les horaires publiés, jamais sur une image. */
function etatBoutique(horaires: Array<{ jour: string; heures: string }>) {
  const maintenant = new Date();
  const jour = maintenant.toLocaleDateString('fr-FR', { weekday: 'long' });
  const duJour = horaires.find(h => h.jour.toLowerCase() === jour);
  if (!duJour || duJour.heures === 'Fermé') return { ouvert: false, texte: 'Fermé aujourd’hui' };
  const minutes = maintenant.getHours() * 60 + maintenant.getMinutes();
  const enMinutes = (v: string) => {
    const m = v.match(/(\d{1,2})h(\d{2})/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  for (const plage of duJour.heures.split('/')) {
    const [debut, fin] = plage.trim().split('–');
    const a = enMinutes(debut ?? ''), b = enMinutes(fin ?? '');
    if (a == null || b == null) continue;
    if (minutes >= a && minutes <= b) return { ouvert: true, texte: `Ouvert · ferme à ${fin.trim()}` };
    if (minutes < a) return { ouvert: false, texte: `Ouvre à ${debut.trim()}` };
  }
  return { ouvert: false, texte: 'Fermé · ouvre demain' };
}

export function Boutiques() {
  const aujourdHui = new Date().toLocaleDateString('fr-FR', { weekday: 'long' });
  const jours = REGLAGES.livraison.joursExpedition;
  const plageExpedition = `${jours[0].toLowerCase()} au ${jours[jours.length - 1].toLowerCase()}`;

  return (
    <main id="contenu" className="mx-auto max-w-[1180px] px-5 py-8 sm:px-6">
      <Fil fils={[{ libelle: 'Nos boutiques' }]} />

      <div className="max-w-2xl">
        <p className="eyebrow">Trois comptoirs, un atelier</p>
        <h1 className="mt-2 text-balance text-[clamp(2rem,4.6vw,3.2rem)]">Passez nous voir,<br />on vous montrera la pièce.</h1>
        <p className="mt-4 text-[1.0625rem] leading-relaxed text-ink-soft">
          Sannois, Franconville, Argenteuil. Les trois boutiques travaillent avec la même viande,
          le même atelier et les mêmes bouchers. Le retrait en ligne se fait dans celle que vous choisissez.
        </p>
      </div>

      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        {BOUTIQUES.map(b => (
          <article key={b.id} className="flex flex-col rounded-[22px] bg-surface p-5 shadow-card sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl">{b.nom}</h2>
                <p className="mt-1 text-sm text-muted">{b.adresse}</p>
              </div>
              <span className={etatBoutique(b.horaires).ouvert ? 'tag tag--success' : 'tag'}>
                {etatBoutique(b.horaires).ouvert ? 'Ouvert' : 'Fermé'}
              </span>
            </div>

            <dl className="mt-4 space-y-1 text-sm">
              {b.horaires.map(h => {
                const estAujourdHui = h.jour.toLowerCase() === aujourdHui;
                return (
                  <div key={h.jour}
                    className={estAujourdHui ? 'flex justify-between rounded-md bg-linen px-2 py-1 font-semibold' : 'flex justify-between px-2 py-1 text-ink-soft'}>
                    <dt>{h.jour}{estAujourdHui && <span className="sr-only"> (aujourd’hui)</span>}</dt>
                    <dd className={h.heures === 'Fermé' ? 'text-muted' : ''}>{h.heures}</dd>
                  </div>
                );
              })}
            </dl>

            <div className="mt-4 flex flex-col gap-2">
              <a href={`tel:${b.telephone.replace(/\s/g, '')}`} className={clsBouton('outline', 'btn--sm')}>
                <Icon name="phone" size={16} /> {b.telephone}
              </a>
              <Link to="/boutique" className={clsBouton('quiet', 'btn--sm')}>
                Commander pour ce comptoir
              </Link>
            </div>
            <p className="mt-4 text-[0.75rem] leading-relaxed text-ink-soft">{b.specialites.join(' · ')}</p>
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted">
              <Icon name="pin" size={13} /> {b.parking}
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted">
              <Icon name="clock" size={13} /> {etatBoutique(b.horaires).texte}
            </p>
          </article>
        ))}
      </div>

      {/* Atelier */}
      <section className="mt-12 overflow-hidden rounded-[24px] bg-ink text-lin-50">
        <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow text-brass-soft">L’atelier</p>
            <h2 className="mt-2 font-display text-3xl text-lin-50">Abattage maison, à Sannois</h2>
            <p className="mt-3 max-w-lg text-[0.9375rem] leading-relaxed text-lin-50/70">
              Nos volailles sont abattues sur place chaque mardi et chaque vendredi. C’est ce qui
              nous permet de vous dire d’où vient chaque bête, et de travailler l’animal entier —
              pas seulement les morceaux qui se vendent vite.
            </p>
            <dl className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
              {[{ k: 'Jours d’abattage', v: 'Mardi & vendredi' }, { k: 'Distance moyenne', v: 'moins de 40 km' }, { k: 'Depuis', v: '1968' }].map(x => (
                <div key={x.k}>
                  <dt className="text-[0.6875rem] uppercase tracking-[0.12em] text-lin-50/50">{x.k}</dt>
                  <dd className="mt-1 font-display text-lg text-lin-50">{x.v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="rounded-2xl bg-lin-50/[0.06] p-6">
            <p className="font-display text-xl text-lin-50">Les horaires qui comptent</p>
            <ul className="mt-4 space-y-3 text-sm text-lin-50/70">
              <li className="flex items-start gap-2.5"><Icon name="clock" size={16} className="mt-0.5 shrink-0 text-brass-soft" /> Commandes en ligne acceptées jusqu’à {REGLAGES.livraison.coupure} pour le lendemain.</li>
              <li className="flex items-start gap-2.5"><Icon name="knife" size={16} className="mt-0.5 shrink-0 text-brass-soft" /> Découpe le matin, retrait à partir de l’ouverture.</li>
              <li className="flex items-start gap-2.5"><Icon name="snow" size={16} className="mt-0.5 shrink-0 text-brass-soft" /> Départs réfrigérés {REGLAGES.livraison.transporteur}, {plageExpedition}.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <Entete titre="Une question avant de venir ?" surtitre="Le comptoir répond"
          texte={`Appelez ${REGLAGES.marque.telephone} — c’est un boucher qui décroche, pas un standard.`} />
      </section>
    </main>
  );
}
